import { useState } from "react";
import { Modal, Form, Button, Alert } from "react-bootstrap";
import { completarPersonaPai } from "../api/bacSarampionAPI.js";
import { guardarFotoOffline, sincronizarFotosBac } from "../../hooks/useSync";
import { comprimirImagen, blobABase64 } from "../utils/imageCompression";

const MOTIVOS_SIMPLE = [
  { value: "contraindicacion", label: "Contraindicación médica" },
  { value: "factores_salud",   label: "Factores R/C servicio de salud" },
  { value: "factores_familia", label: "Factores R/C la familia" },
  { value: "sin_equipo",       label: "No hay equipo vacunador" },
  { value: "no_aplica",        label: "No aplica" },
  { value: "otros",             label: "Otros" },
];

const FORM_VACIO = {
  nombre: "", tipo_identificacion: "", numero_identificacion: "", edad: "", unidad_edad: "Años",
  tiene_vacuna_sarampion: "", se_aplico_vacuna: "", motivo_no_vacunada: "",
  nombre_vacunador: "", nombre_biologico: "", lote: "",
};

export default function CompletarPersonaModal({ show, onClose, registro, onCompletado }) {
  const [personaId, setPersonaId] = useState("");
  const [yaTeniaEvidencia, setYaTeniaEvidencia] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [fotoA, setFotoA] = useState(null);
  const [fotoB, setFotoB] = useState(null);
  const [fotoPaiweb, setFotoPaiweb] = useState(null);
  const [comprimiendo, setComprimiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  if (!registro) return null;

  // Personas que se pueden actualizar en esta revisita:
  // - Sin carnet ni validación PAIWEB (renuentes / evidencia pendiente)
  // - CON carnet, pero encontradas SIN la vacuna y sin aplicarla en el
  //   barrido — quedaron pendientes, y esta revisita es justo para
  //   actualizarlas si ya se vacunaron.
  const incompletas = (registro.carnets || []).filter(c =>
    c.presenta_evidencia !== "Si"
    || (c.tiene_vacuna_sarampion === "No" && c.se_aplico_vacuna === "No")
  );

  const elegirPersona = (id) => {
    setPersonaId(id);
    const c = incompletas.find(x => String(x.id) === String(id));
    if (c) {
      const teniaEvidencia = c.presenta_evidencia === "Si";
      setYaTeniaEvidencia(teniaEvidencia);
      setForm({
        ...FORM_VACIO,
        nombre: c.nombre, tipo_identificacion: c.tipo_identificacion, numero_identificacion: c.numero_identificacion,
        edad: c.edad || "", unidad_edad: c.unidad_edad || "Años",
        // Si ya tenía carnet, es porque quedó pendiente de vacunar — ya
        // se sabe que no tenía la vacuna, se prellena para no volver a
        // preguntarlo (la persona solo confirma que ya se le aplicó).
        tiene_vacuna_sarampion: teniaEvidencia ? "No" : "",
      });
    }
  };

  // Las fotos del carnet solo se piden cuando la persona presenta
  // evidencia por PRIMERA VEZ en esta revisita (antes era renuente/no
  // presentaba) — y ahí solo si tiene 6 años o menos, mismo criterio
  // que en el formulario principal. Quien ya tenía carnet de antes
  // (pendiente solo de vacunarse) no necesita volver a subir fotos.
  const requiereFotoCarnet = () => {
    if (yaTeniaEvidencia) return false;
    const n = parseFloat(form.edad);
    if (isNaN(n)) return true;
    return form.unidad_edad === "Meses" ? true : n <= 6;
  };

  const handleFoto = async (e, lado) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComprimiendo(true);
    try {
      const comprimida = await comprimirImagen(file, { maxAncho: 1280, calidad: 0.7 });
      const base64 = await blobABase64(comprimida);
      const datos = { base64, nombre: file.name };
      if (lado === "a") setFotoA(datos); else if (lado === "b") setFotoB(datos); else setFotoPaiweb(datos);
    } catch {
      setError("No se pudo procesar la foto");
    } finally {
      setComprimiendo(false);
    }
  };

  const cerrar = () => {
    setPersonaId(""); setYaTeniaEvidencia(false); setForm(FORM_VACIO);
    setFotoA(null); setFotoB(null); setFotoPaiweb(null); setError("");
    onClose();
  };

  const guardar = async () => {
    if (!personaId) { setError("Elige a la persona que vas a completar"); return; }
    if (!form.nombre || !form.tipo_identificacion || !form.numero_identificacion || !form.edad) { setError("Completa nombre, documento y edad"); return; }
    if (requiereFotoCarnet() && !fotoPaiweb && (!fotoA || !fotoB)) { setError("Faltan las 2 caras del carnet, o en su lugar la foto de validación PAIWEB"); return; }
    if (!form.tiene_vacuna_sarampion) { setError("Falta si ya tiene la vacuna de sarampión"); return; }
    if (form.tiene_vacuna_sarampion === "No") {
      if (!form.se_aplico_vacuna) { setError("Falta si se le aplicó la vacuna ahora"); return; }
      if (form.se_aplico_vacuna === "Si" && (!form.nombre_vacunador || !form.nombre_biologico || !form.lote)) {
        setError("Faltan datos de vacunador, biológico y lote"); return;
      }
      if (form.se_aplico_vacuna === "No" && !form.motivo_no_vacunada) { setError("Falta el motivo"); return; }
    }

    setError("");
    setGuardando(true);
    try {
      await completarPersonaPai(registro.id, { carnet_id: personaId, ...form });

      const codigoVivienda = registro.visita?.codigo_vivienda;
      if (fotoA) await guardarFotoOffline(codigoVivienda, fotoA.base64, fotoA.nombre, "carnet_a", form.numero_identificacion);
      if (fotoB) await guardarFotoOffline(codigoVivienda, fotoB.base64, fotoB.nombre, "carnet_b", form.numero_identificacion);
      if (fotoPaiweb) await guardarFotoOffline(codigoVivienda, fotoPaiweb.base64, fotoPaiweb.nombre, "carnet_paiweb", form.numero_identificacion);
      sincronizarFotosBac().catch(() => {});

      onCompletado();
      cerrar();
    } catch (err) {
      setError(err.response?.data?.message || "No se pudo completar — revisa tu conexión");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={cerrar} centered scrollable>
      <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #16342a, #2d6a4f)", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>🔁 Completar revisita — {registro.visita?.codigo_vivienda}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {incompletas.length === 0 ? (
          <p className="text-muted mb-0">Ya no hay personas pendientes en este registro.</p>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700 }}>¿A quién vas a completar?</Form.Label>
              <Form.Select value={personaId} onChange={e => elegirPersona(e.target.value)}>
                <option value="">Selecciona...</option>
                {incompletas.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.presenta_evidencia !== "Si" ? "🪪 Sin carnet — " : "💉 Pendiente de vacunar — "}
                    {c.nombre} — {c.tipo_identificacion} {c.numero_identificacion}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {personaId && (
              <>
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <Form.Control size="sm" placeholder="Nombre" value={form.nombre}
                      onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <Form.Control size="sm" placeholder="Tipo documento" value={form.tipo_identificacion}
                      onChange={e => setForm(p => ({ ...p, tipo_identificacion: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <Form.Control size="sm" placeholder="N° documento" value={form.numero_identificacion}
                      onChange={e => setForm(p => ({ ...p, numero_identificacion: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <Form.Control size="sm" type="number" placeholder="Edad" value={form.edad}
                      onChange={e => setForm(p => ({ ...p, edad: e.target.value }))} />
                  </div>
                  <div className="col-6">
                    <Form.Select size="sm" value={form.unidad_edad} onChange={e => setForm(p => ({ ...p, unidad_edad: e.target.value }))}>
                      <option value="Años">Años</option>
                      <option value="Meses">Meses</option>
                    </Form.Select>
                  </div>
                </div>

                {requiereFotoCarnet() ? (
                  <div className="row g-2 mb-2">
                    {[["a", "Cara A", fotoA], ["b", "Cara B", fotoB], ["paiweb", "PAIWEB (opcional)", fotoPaiweb]].map(([lado, label, foto]) => (
                      <div className="col-4" key={lado}>
                        <div className="d-flex align-items-center gap-1 p-2 rounded" style={{ background: "#f9fafb", border: "1px dashed #dee2e6" }}>
                          <div className="rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 34, height: 34, background: "#eef1e7", overflow: "hidden" }}>
                            {foto ? <img src={foto.base64} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "🪪"}
                          </div>
                          <label className="btn btn-outline-secondary btn-sm mb-0" style={{ fontSize: 9.5, padding: "2px 5px" }}>
                            {comprimiendo ? "..." : label}
                            {/* Sin "capture" — deja elegir cámara o galería/fototeca */}
                            <input type="file" accept="image/*" onChange={e => handleFoto(e, lado)} disabled={comprimiendo} className="d-none" />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="alert alert-secondary py-2 px-3 mb-2" style={{ fontSize: 11.5 }}>
                    ℹ️ {yaTeniaEvidencia
                      ? "Ya tiene carnet registrado de la visita anterior — no hace falta volver a subir fotos."
                      : "Con más de 6 años no hace falta foto del carnet."}
                  </div>
                )}

                <div className="mb-2">
                  <Form.Label style={{ fontSize: 11 }}>¿Ya tiene la vacuna de sarampión?</Form.Label>
                  <div className="d-flex gap-2">
                    {["Si", "No"].map(v => (
                      <Button key={v} type="button" size="sm" className="flex-grow-1"
                        variant={form.tiene_vacuna_sarampion === v ? (v === "Si" ? "success" : "warning") : "outline-secondary"}
                        onClick={() => setForm(p => ({ ...p, tiene_vacuna_sarampion: v, se_aplico_vacuna: "", motivo_no_vacunada: "", nombre_vacunador: "", nombre_biologico: "", lote: "" }))}>{v}</Button>
                    ))}
                  </div>
                </div>

                {form.tiene_vacuna_sarampion === "No" && (
                  <div className="mb-2">
                    <Form.Label style={{ fontSize: 11 }}>¿Se le aplicó ahora?</Form.Label>
                    <div className="d-flex gap-2">
                      {["Si", "No"].map(v => (
                        <Button key={v} type="button" size="sm" className="flex-grow-1"
                          variant={form.se_aplico_vacuna === v ? (v === "Si" ? "success" : "danger") : "outline-secondary"}
                          onClick={() => setForm(p => ({ ...p, se_aplico_vacuna: v }))}>{v}</Button>
                      ))}
                    </div>
                  </div>
                )}

                {form.se_aplico_vacuna === "Si" && (
                  <div className="p-2 rounded mb-2" style={{ background: "#e4f2e8" }}>
                    <div className="row g-2">
                      <div className="col-12">
                        <Form.Control size="sm" placeholder="Vacunador" value={form.nombre_vacunador} onChange={e => setForm(p => ({ ...p, nombre_vacunador: e.target.value }))} />
                      </div>
                      <div className="col-6">
                        <Form.Control size="sm" placeholder="Biológico" value={form.nombre_biologico} onChange={e => setForm(p => ({ ...p, nombre_biologico: e.target.value }))} />
                      </div>
                      <div className="col-6">
                        <Form.Control size="sm" placeholder="Lote" value={form.lote} onChange={e => setForm(p => ({ ...p, lote: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                )}

                {form.se_aplico_vacuna === "No" && (
                  <div className="mb-2">
                    <Form.Select size="sm" value={form.motivo_no_vacunada} onChange={e => setForm(p => ({ ...p, motivo_no_vacunada: e.target.value }))}>
                      <option value="">Motivo...</option>
                      {MOTIVOS_SIMPLE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Form.Select>
                  </div>
                )}
              </>
            )}

            {error && <Alert variant="danger" className="py-2" style={{ fontSize: 12.5 }}>{error}</Alert>}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={cerrar}>Cancelar</Button>
        {incompletas.length > 0 && (
          <Button variant="success" onClick={guardar} disabled={guardando} style={{ background: "#16342a", border: "none" }}>
            {guardando ? "Guardando..." : "Completar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}